// src/Views/ProfileView.js
import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import AuthService from '../services/AuthService';
import { AuthContext } from '../contexts/AuthContext';
import EditNameModal from '../components/EditNameModal';
import ChangeEmailModal from '../components/ChangeEmailModal';
import {
  MAX_CONTENT_WIDTH,
  BORDER_RADIUS,
  SPACING_TIGHT,
  SPACING_ELEMENT,
  SPACING_COMPACT,
  SPACING_COMPONENT,
  SPACING_BUTTON,
  SPACING_CARD,
  SPACING_CARD_BODY,
  SPACING_GROUP,
  SPACING_SECTION,
} from '../constants/layout';
import {
  FONT_TITLE2,
  FONT_TITLE2_MOBILE,
  FONT_TITLE3,
  FONT_BODY,
  FONT_FOOTNOTE,
} from '../constants/typography';

function ProfileView() {
  const { user, loading, markAccountDeleted } = useContext(AuthContext);
  const [error, setError] = useState('');


  // Profile data
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');


  // Change Name modal
  const [showChangeName, setShowChangeName] = useState(false);

  // Change Email modal
  const [showChangeEmail, setShowChangeEmail] = useState(false);

  // Inline phone editing
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);

  // Change Password modal
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Delete Account modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const navigate = useNavigate();

  const functions = getFunctions(getApp(), 'us-central1');

  // Fetch contact info from getUserContactInfo cloud function
  const fetchContactInfo = async () => {
    if (!user) return;
    try {
      const getUserContactInfo = httpsCallable(functions, 'getUserContactInfo');
      const result = await getUserContactInfo({ platform: 'web' });
      const data = result.data;
      if (data) {
        setFirstName(data.firstName || '');
        setLastName(data.lastName || '');
        setContactEmail(data.contactEmail || user.email || '');
        setContactPhone(data.contactPhone || '');
      }
    } catch (err) {
      console.error('Error fetching contact info:', err);
      // Fall back to Firebase Auth data
      setContactEmail(user.email || '');
    }

  };

  useEffect(() => {
    if (user) {
      fetchContactInfo();
    } else {
      setFirstName('');
      setLastName('');
      setContactEmail('');
      setContactPhone('');
    }
  }, [user]);

  const handleSignOut = async () => {
    try {
      await AuthService.signOut();
    } catch (err) {
      setError(err.message);
    }
  };


  // Inline phone editing
  const handleStartPhoneEdit = () => {
    setPhoneInput(contactPhone);
    setEditingPhone(true);
  };

  const handleSavePhone = async () => {
    if (phoneInput === contactPhone) {
      setEditingPhone(false);
      return;
    }
    setPhoneSaving(true);
    try {
      const updateUserContactPhone = httpsCallable(functions, 'updateUserContactPhone');
      await updateUserContactPhone({ phone: phoneInput });
      setContactPhone(phoneInput);
      setEditingPhone(false);
    } catch (err) {
      console.error('Failed to save phone:', err);
    } finally {
      setPhoneSaving(false);
    }
  };

  // Change Password handlers
  const handleChangePassword = async () => {
    const hasPassword = AuthService.hasPasswordProvider();
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }
    if (hasPassword && newPassword === currentPassword) {
      setPasswordError('New password must be different from current password.');
      return;
    }

    setPasswordLoading(true);
    setPasswordError('');
    try {
      if (hasPassword) {
        await AuthService.reauthenticate(currentPassword);
        await AuthService.updatePassword(newPassword);
      } else {
        await AuthService.addPasswordAuthentication(newPassword);
      }
      setPasswordSuccess(true);
      setTimeout(() => {
        setShowChangePassword(false);
        setPasswordSuccess(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
      }, 1500);
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setPasswordError('Current password is incorrect.');
      } else if (err.code === 'auth/weak-password') {
        setPasswordError('Password is too weak. Please use a stronger password.');
      } else {
        setPasswordError(err.message || 'Failed to update password.');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  // Delete Account handlers
  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    setDeleteError('');
    try {
      if (AuthService.hasAppleProvider()) {
        // AuthService returns the platform-specific handoff without opening an
        // unrelated password or Google reauthentication flow.
        await AuthService.deleteAccount();
        return;
      } else if (AuthService.hasPasswordProvider()) {
        await AuthService.reauthenticate(deletePassword);
      } else {
        await AuthService.reauthenticateWithGoogle();
      }
      await AuthService.deleteAccount();
      markAccountDeleted();
      navigate('/');
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setDeleteError('Incorrect password. Please try again.');
      } else if (err.code === 'auth/popup-closed-by-user') {
        setDeleteError('Google sign-in was cancelled.');
      } else {
        setDeleteError(err.message || 'Failed to delete account. Please try again.');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-container loading">
        <p style={{ color: 'var(--color-text-secondary)', textAlign: 'center' }}>Loading...</p>
        <style jsx>{`
          .profile-container { max-width: ${MAX_CONTENT_WIDTH}; margin: 0 auto; padding: 0 ${SPACING_CARD} ${SPACING_SECTION}; }
          .profile-container.loading { display: flex; justify-content: center; align-items: center; min-height: 400px; }
        `}</style>
      </div>
    );
  }

  if (user) {
    const displayName = firstName && lastName
      ? `${firstName} ${lastName}`
      : firstName || user.displayName || '—';

    const memberSince = user.metadata?.creationTime
      ? new Date(user.metadata.creationTime).toLocaleDateString('en-US', {
          year: 'numeric', month: 'long', day: 'numeric',
        })
      : null;

    return (
      <div className="profile-container">
        <div className="profile-header">
          <h1 className="section-title">Profile</h1>
        </div>

        {/* Account Info — matching iOS SignedInAccountView */}
        <div className="info-section">
          <button className="info-row-button" onClick={() => setShowChangeName(true)}>
            <span className="info-label">Name</span>
            <span className="info-value">{displayName}</span>
          </button>

          <button className="info-row-button" onClick={() => setShowChangeEmail(true)}>
            <span className="info-label">Email</span>
            <span className="info-value">{contactEmail || user.email || '—'}</span>
          </button>

          {editingPhone ? (
            <div className="info-row phone-edit-row">
              <span className="info-label">Phone</span>
              <div className="phone-edit-controls">
                <input
                  type="tel"
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value)}
                  className="phone-input"
                  placeholder="Phone number"
                  autoFocus
                  autoComplete="tel"
                />
                {phoneSaving ? (
                  <div className="spinner-small"></div>
                ) : (
                  <button
                    onClick={handleSavePhone}
                    className="phone-save-button"
                    disabled={!phoneInput.trim()}
                  >
                    Save
                  </button>
                )}
              </div>
            </div>
          ) : (
            <button className="info-row-button" onClick={handleStartPhoneEdit}>
              <span className="info-label">Phone</span>
              {contactPhone ? (
                <span className="info-value">{contactPhone}</span>
              ) : (
                <span className="info-value info-value-placeholder">Add phone number</span>
              )}
            </button>
          )}

        </div>

        {memberSince && (
          <div className="member-since">
            Member since {memberSince}
          </div>
        )}

        {/* Account Actions */}
        <div className="actions-section">
          <button
            onClick={() => {
              setShowChangePassword(true);
              setPasswordError('');
              setPasswordSuccess(false);
              setCurrentPassword('');
              setNewPassword('');
              setConfirmNewPassword('');
            }}
            className="action-row-button"
          >
            {AuthService.hasPasswordProvider() ? 'Change Password' : 'Set Password'}
          </button>
          <button onClick={handleSignOut} className="action-row-button">
            Log Out
          </button>
          <button
            onClick={() => { setShowDeleteConfirm(true); setDeleteError(''); setDeletePassword(''); }}
            className="action-row-button action-destructive"
          >
            Delete Account
          </button>
        </div>

        <EditNameModal
          isOpen={showChangeName}
          onClose={() => setShowChangeName(false)}
          initialFirstName={firstName}
          initialLastName={lastName}
          onSaved={(f, l) => { setFirstName(f); setLastName(l); }}
        />

        <ChangeEmailModal
          isOpen={showChangeEmail}
          onClose={() => setShowChangeEmail(false)}
          currentEmail={contactEmail || user.email}
          onSaved={(e) => setContactEmail(e)}
        />

        {/* Change Password Modal */}
        {showChangePassword && (
          <div className="modal-overlay" onClick={() => !passwordLoading && setShowChangePassword(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{AuthService.hasPasswordProvider() ? 'Change Password' : 'Set Password'}</h3>
                <button className="modal-close" onClick={() => !passwordLoading && setShowChangePassword(false)} disabled={passwordLoading}>
                  <span className="material-icons">close</span>
                </button>
              </div>
              <div className="modal-body">
                {passwordSuccess ? (
                  <div className="success-message">
                    <span className="material-icons success-icon">check_circle</span>
                    <p>Password updated successfully.</p>
                  </div>
                ) : (
                  <>
                    {AuthService.hasPasswordProvider() && (
                      <div className="form-group">
                        <label className="form-label">Current password</label>
                        <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
                          className="form-input" placeholder="Current password" disabled={passwordLoading} autoComplete="current-password" autoFocus />
                      </div>
                    )}
                    <div className="form-group">
                      <label className="form-label">New password</label>
                      <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                        className="form-input" placeholder="New password (min 6 characters)" disabled={passwordLoading}
                        autoComplete="new-password" autoFocus={!AuthService.hasPasswordProvider()} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Confirm new password</label>
                      <input type="password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)}
                        className="form-input" placeholder="Confirm new password" disabled={passwordLoading} autoComplete="new-password" />
                    </div>
                    {passwordError && <p className="form-error">{passwordError}</p>}
                  </>
                )}
              </div>
              {!passwordSuccess && (
                <div className="modal-footer">
                  <button onClick={() => setShowChangePassword(false)} className="cancel-button" disabled={passwordLoading}>Cancel</button>
                  <button onClick={handleChangePassword} className="save-button"
                    disabled={passwordLoading || !newPassword || newPassword !== confirmNewPassword || newPassword.length < 6
                      || (AuthService.hasPasswordProvider() && !currentPassword)}>
                    {passwordLoading ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Delete Account Modal */}
        {showDeleteConfirm && (
          <div className="modal-overlay" onClick={() => !isDeleting && setShowDeleteConfirm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Delete Account</h3>
                <button className="modal-close" onClick={() => !isDeleting && setShowDeleteConfirm(false)} disabled={isDeleting}>
                  <span className="material-icons">close</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="delete-warning">
                  <span className="material-icons delete-warning-icon">warning</span>
                  <p>This will permanently delete your account and all associated data. Your order history will be anonymized for record-keeping. This action cannot be undone.</p>
                </div>
                {AuthService.hasAppleProvider() ? (
                  <p className="delete-reauth-note">
                    Sign in with Apple revocation must be completed in the
                    Koral Krush app on iPhone or iPad, or through support.
                  </p>
                ) : AuthService.hasPasswordProvider() ? (
                  <div className="form-group">
                    <label className="form-label">Enter your password to confirm</label>
                    <input type="password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)}
                      className="form-input" placeholder="Password" disabled={isDeleting} />
                  </div>
                ) : (
                  <p className="delete-reauth-note">You will be asked to sign in with Google to confirm.</p>
                )}
                {deleteError && <p className="form-error">{deleteError}</p>}
              </div>
              <div className="modal-footer">
                <button onClick={() => setShowDeleteConfirm(false)} className="cancel-button" disabled={isDeleting}>Cancel</button>
                <button onClick={handleDeleteAccount} className="delete-confirm-button"
                  disabled={
                    isDeleting
                    || (
                      !AuthService.hasAppleProvider()
                      && AuthService.hasPasswordProvider()
                      && !deletePassword
                    )
                  }>
                  {isDeleting ? 'Deleting...' : 'Delete My Account'}
                </button>
              </div>
            </div>
          </div>
        )}

        <style jsx>{`
          .profile-container {
            max-width: ${MAX_CONTENT_WIDTH};
            margin: 0 auto;
            padding: ${SPACING_CARD_BODY} ${SPACING_CARD} ${SPACING_SECTION};
          }

          .profile-header { margin-bottom: ${SPACING_CARD}; }

          .section-title {
            font-size: ${FONT_TITLE2};
            font-family: var(--font-family-base);
            font-weight: 600;
            margin: 0;
            color: var(--color-text-primary);
          }

          /* Info Section */
          .info-section {
            background-color: var(--color-surface, var(--color-card-background, #fff));
            border: 1px solid var(--color-border);
            border-radius: ${BORDER_RADIUS};
            overflow: hidden;
            margin-bottom: ${SPACING_CARD_BODY};
          }

          .info-row-button {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: ${SPACING_BUTTON} ${SPACING_CARD};
            background: none;
            border: none;
            border-bottom: 1px solid var(--color-border);
            cursor: pointer;
            text-align: left;
            transition: background-color 0.15s ease;
          }

          .info-row-button:last-of-type {
            border-bottom: none;
          }

          .info-row-button:hover {
            background-color: var(--color-surface-muted, rgba(0,0,0,0.02));
          }

          .info-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: ${SPACING_BUTTON} ${SPACING_CARD};
            border-bottom: 1px solid var(--color-border);
          }

          .info-label {
            font-size: ${FONT_BODY};
            color: var(--color-text-primary);
            flex-shrink: 0;
          }

          .info-value {
            font-size: ${FONT_BODY};
            color: var(--color-text-primary);
            text-align: right;
          }

          .info-value-placeholder {
            color: var(--color-text-secondary);
          }

          .phone-edit-row {
            border-bottom: none;
          }

          .phone-edit-controls {
            display: flex;
            align-items: center;
            gap: ${SPACING_ELEMENT};
          }

          .phone-input {
            padding: ${SPACING_ELEMENT} ${SPACING_COMPONENT};
            border: 1px solid var(--color-border);
            border-radius: ${BORDER_RADIUS};
            font-size: ${FONT_BODY};
            color: var(--color-text-primary);
            background-color: var(--color-surface);
            width: 180px;
            text-align: right;
          }

          .phone-input:focus {
            outline: none;
            border-color: var(--color-accent);
          }

          .phone-save-button {
            padding: ${SPACING_ELEMENT} ${SPACING_BUTTON};
            background-color: var(--color-accent);
            color: var(--color-accent-text);
            border: none;
            border-radius: ${BORDER_RADIUS};
            font-size: ${FONT_FOOTNOTE};
            font-weight: 600;
            cursor: pointer;
          }

          .phone-save-button:disabled { opacity: 0.5; cursor: not-allowed; }

          .member-since {
            font-size: ${FONT_FOOTNOTE};
            color: var(--color-text-secondary);
            padding: ${SPACING_ELEMENT} ${SPACING_TIGHT} 0;
          }

          /* Account Actions */
          .actions-section {
            background-color: var(--color-surface, var(--color-card-background, #fff));
            border: 1px solid var(--color-border);
            border-radius: ${BORDER_RADIUS};
            overflow: hidden;
            margin-top: ${SPACING_CARD_BODY};
          }

          .action-row-button {
            width: 100%;
            display: block;
            padding: ${SPACING_BUTTON} ${SPACING_CARD};
            background: none;
            border: none;
            border-bottom: 1px solid var(--color-border);
            cursor: pointer;
            text-align: left;
            font-size: ${FONT_BODY};
            color: var(--color-accent);
            transition: background-color 0.15s ease;
          }

          .action-row-button:last-child {
            border-bottom: none;
          }

          .action-row-button:hover {
            background-color: var(--color-surface-muted, rgba(0,0,0,0.02));
          }

          .action-row-button.action-destructive {
            color: #ef4444;
          }

          /* Modals */
          .modal-overlay {
            position: fixed; top: 0; left: 0; right: 0; bottom: 0;
            background-color: rgba(0,0,0,0.5);
            display: flex; align-items: center; justify-content: center;
            z-index: 1000; padding: ${SPACING_CARD};
          }

          .modal-content {
            background-color: var(--color-background);
            border-radius: ${BORDER_RADIUS};
            max-width: 500px; width: 100%;
            box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
          }

          .modal-header {
            display: flex; justify-content: space-between; align-items: center;
            padding: ${SPACING_CARD} ${SPACING_CARD_BODY};
            border-bottom: 1px solid var(--color-border);
          }

          .modal-header h3 {
            font-size: ${FONT_TITLE3}; font-weight: 600; margin: 0;
            color: var(--color-text-primary);
          }

          .modal-close {
            background: none; border: none; cursor: pointer;
            padding: 4px; color: var(--color-text-secondary);
          }

          .modal-close:hover { color: var(--color-text-primary); }
          .modal-close .material-icons { font-size: 24px; }

          .modal-body { padding: ${SPACING_CARD_BODY}; }

          .modal-footer {
            display: flex; justify-content: flex-end; gap: ${SPACING_COMPONENT};
            padding: ${SPACING_CARD} ${SPACING_CARD_BODY};
            border-top: 1px solid var(--color-border);
          }

          .form-group { margin-bottom: ${SPACING_CARD}; }
          .form-group:last-child { margin-bottom: 0; }

          .form-label {
            display: block;
            font-size: ${FONT_FOOTNOTE}; font-weight: 600;
            color: var(--color-text-primary);
            margin-bottom: ${SPACING_ELEMENT};
          }

          .form-input {
            width: 100%; padding: ${SPACING_COMPONENT};
            border: 1px solid var(--color-border);
            border-radius: ${BORDER_RADIUS};
            font-size: ${FONT_BODY};
            color: var(--color-text-primary);
            background-color: var(--color-surface);
          }

          .form-input:focus { outline: none; border-color: var(--color-accent); }
          .form-input:disabled { opacity: 0.6; cursor: not-allowed; }

          .form-static {
            font-size: ${FONT_BODY};
            color: var(--color-text-secondary);
            padding: ${SPACING_COMPONENT} 0 0;
          }

          .form-error {
            color: #ef4444;
            font-size: ${FONT_FOOTNOTE};
            margin: ${SPACING_BUTTON} 0 0;
          }

          .cancel-button, .save-button {
            padding: ${SPACING_COMPACT} ${SPACING_CARD}; border: none;
            border-radius: ${BORDER_RADIUS};
            font-size: ${FONT_BODY}; font-weight: 600;
            cursor: pointer;
          }

          .cancel-button {
            background-color: transparent;
            color: var(--color-text-secondary);
            border: 1px solid var(--color-border);
          }

          .cancel-button:hover:not(:disabled) { background-color: var(--color-surface-muted); }

          .save-button {
            background-color: var(--color-accent);
            color: var(--color-accent-text);
          }

          .save-button:hover:not(:disabled) { background-color: var(--color-accent-hover); }
          .save-button:disabled, .cancel-button:disabled { opacity: 0.5; cursor: not-allowed; }

          .success-message {
            display: flex; flex-direction: column; align-items: center;
            gap: ${SPACING_ELEMENT}; padding: ${SPACING_CARD_BODY} 0;
          }

          .success-icon { font-size: 48px; color: #22c55e; }

          .success-message p {
            margin: 0; color: var(--color-text-primary); font-weight: 600; text-align: center;
          }

          .delete-warning {
            display: flex; gap: ${SPACING_COMPONENT}; padding: ${SPACING_BUTTON};
            background-color: #fef2f2;
            border-radius: ${BORDER_RADIUS};
            margin-bottom: ${SPACING_BUTTON};
          }

          .delete-warning-icon { color: #ef4444; flex-shrink: 0; }

          .delete-warning p {
            margin: 0; font-size: ${FONT_FOOTNOTE};
            color: #991b1b; line-height: 1.5;
          }

          .delete-reauth-note {
            font-size: ${FONT_FOOTNOTE};
            color: var(--color-text-secondary);
            margin: 0 0 ${SPACING_BUTTON};
          }

          .delete-confirm-button {
            padding: ${SPACING_COMPACT} ${SPACING_CARD};
            background-color: #ef4444; color: white;
            border: none; border-radius: ${BORDER_RADIUS};
            font-size: ${FONT_BODY}; font-weight: 600; cursor: pointer;
          }

          .delete-confirm-button:hover:not(:disabled) { background-color: #dc2626; }
          .delete-confirm-button:disabled { opacity: 0.5; cursor: not-allowed; }

          .spinner {
            width: 32px; height: 32px;
            border: 3px solid rgba(255,255,255,0.3);
            border-top-color: white;
            border-radius: 50%;
            animation: spin 0.6s linear infinite;
          }

          .spinner-small {
            width: 20px; height: 20px;
            border: 2px solid var(--color-border);
            border-top-color: var(--color-accent);
            border-radius: 50%;
            animation: spin 0.6s linear infinite;
          }

          @keyframes spin { to { transform: rotate(360deg); } }

          @media (max-width: 600px) {
            .section-title { font-size: ${FONT_TITLE2_MOBILE}; }
            .phone-input { width: 140px; }
          }
        `}</style>
      </div>
    );
  }

  // Not signed in — redirect to login
  navigate('/login');
  return null;
}

export default ProfileView;
